/**
 * Filters — faceted search filters. The URL is the source of truth: every
 * control reads from {@link SearchParams} and reports changes up via
 * `onChange`, which the page merges into the URL search params.
 *
 * Controls:
 *  - jurisdiction multi-select (UUIDs supplied by the caller)
 *  - meeting-body free text
 *  - meeting-date range (from / to, YYYY-MM-DD)
 *  - document-kind multi-select
 *  - "Clear all" to reset every facet (leaves query + mode intact)
 *
 * All inputs are labelled and keyboard reachable (CLAUDE.md §10).
 */
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DocumentKindEnum } from "@records-monitor/shared";
import { useId, type ReactElement } from "react";
import type { SearchParams } from "./urlState";

const DOCUMENT_KINDS = DocumentKindEnum.options;

export interface JurisdictionOption {
  id: string;
  name: string;
}

export interface FiltersProps {
  params: SearchParams;
  /** Available jurisdictions to choose from (loaded by the caller). */
  jurisdictions: readonly JurisdictionOption[];
  /** Merge a partial patch into the URL filter state. */
  onChange: (patch: Partial<SearchParams>) => void;
  /** Reset every facet. */
  onClearAll: () => void;
}

export function Filters({
  params,
  jurisdictions,
  onChange,
  onClearAll,
}: FiltersProps): ReactElement {
  const jurisdictionLabelId = useId();
  const kindLabelId = useId();

  return (
    <Box
      component="section"
      aria-label="Filters"
      sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Typography variant="subtitle1" component="h2" gutterBottom>
        Filters
      </Typography>
      <Stack spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel id={jurisdictionLabelId}>Jurisdiction</InputLabel>
          <Select
            labelId={jurisdictionLabelId}
            multiple
            label="Jurisdiction"
            value={params.jurisdictionIds ?? []}
            input={<OutlinedInput label="Jurisdiction" />}
            onChange={(event) => {
              const value = event.target.value;
              const ids = typeof value === "string" ? value.split(",") : value;
              onChange({ jurisdictionIds: ids.length > 0 ? ids : undefined });
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((id) => {
                  const match = jurisdictions.find((j) => j.id === id);
                  return <Chip key={id} size="small" label={match?.name ?? id} />;
                })}
              </Box>
            )}
          >
            {jurisdictions.map((j) => (
              <MenuItem key={j.id} value={j.id}>
                {j.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size="small"
          label="Body"
          placeholder="e.g. City Council"
          value={params.bodyNames?.[0] ?? ""}
          onChange={(event) => {
            const text = event.target.value;
            onChange({ bodyNames: text.length > 0 ? [text] : undefined });
          }}
        />

        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            label="From date"
            placeholder="YYYY-MM-DD"
            value={params.dateFrom ?? ""}
            onChange={(event) => {
              const text = event.target.value.trim();
              onChange({ dateFrom: text || undefined });
            }}
          />
          <TextField
            size="small"
            label="To date"
            placeholder="YYYY-MM-DD"
            value={params.dateTo ?? ""}
            onChange={(event) => {
              const text = event.target.value.trim();
              onChange({ dateTo: text || undefined });
            }}
          />
        </Stack>

        <FormControl fullWidth size="small">
          <InputLabel id={kindLabelId}>Document kind</InputLabel>
          <Select
            labelId={kindLabelId}
            multiple
            label="Document kind"
            value={params.documentKinds ?? []}
            input={<OutlinedInput label="Document kind" />}
            onChange={(event) => {
              const value = event.target.value;
              const kinds = (typeof value === "string" ? value.split(",") : value) as string[];
              onChange({
                documentKinds:
                  kinds.length > 0 ? (kinds as SearchParams["documentKinds"]) : undefined,
              });
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((kind) => (
                  <Chip key={kind} size="small" label={kind} />
                ))}
              </Box>
            )}
          >
            {DOCUMENT_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {kind}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={onClearAll}>
          Clear all
        </Button>
      </Stack>
    </Box>
  );
}
